import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useRejectFSMSubmissionMutation } from "../api/fsmApi";

interface Props {
  submissionId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const REJECTION_TEMPLATES = [
  "fsmReject.templates.blurry",
  "fsmReject.templates.noGps",
  "fsmReject.templates.wrongLocation",
  "fsmReject.templates.notEnoughPhotos",
  "fsmReject.templates.selfieInvalid",
  "fsmReject.templates.other",
] as const;

export default function FSMRejectModal({ submissionId, onClose, onSuccess, onError }: Props) {
  const { t } = useTranslation("PendingActionsPage");
  const [reason, setReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectFSMSubmission] = useRejectFSMSubmissionMutation();

  useEffect(function escapeKeyHandler() {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  useEffect(function autoFocusTextarea() {
    document.getElementById("fsm-reject-reason")?.focus();
  }, []);

  function handleTemplateClick(templateKey: string) {
    if (templateKey === "fsmReject.templates.other") {
      setReason("");
      document.getElementById("fsm-reject-reason")?.focus();
      return;
    }
    setReason(t(templateKey));
  }

  async function handleSubmit() {
    if (!reason.trim() || isRejecting) return;
    setIsRejecting(true);
    try {
      await rejectFSMSubmission({ id: submissionId, reason: reason.trim() }).unwrap();
      onSuccess(t("fsmReview.rejectSuccess"));
    } catch (err) {
      onError(extractErrorMessage(err, "errors.fsmReject", t));
    } finally {
      setIsRejecting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[10200] flex items-center justify-center surface-backdrop" onClick={onClose}>
      <div
        className="w-[480px] surface-base-fill radius-lg p-6 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-display-2xs content-base-primary">{t("fsmReject.title")}</h2>

        <div className="flex flex-wrap gap-2">
          {REJECTION_TEMPLATES.map((key) => (
            <button
              key={key}
              type="button"
              className="px-3 py-1.5 text-body-regular-sm radius-sm surface-secondary-fill content-base-secondary hover:surface-tertiary-fill transition-colors cursor-pointer"
              onClick={() => handleTemplateClick(key)}
            >
              {t(key)}
            </button>
          ))}
        </div>

        <Input
          id="fsm-reject-reason"
          isTextarea
          label={t("fsmReject.reasonLabel")}
          placeholder={t("fsmReject.reasonPlaceholder")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        <div className="flex gap-3 justify-end">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isRejecting}>
            {t("fsmReject.cancel")}
          </Button>
          <Button variant="danger" size="md" onClick={handleSubmit} disabled={!reason.trim() || isRejecting}>
            {t("fsmReject.submit")}
          </Button>
        </div>
      </div>
    </div>
  );
}
